import type { BuildStep } from '../../model/use-agent-panel-store';
import { Check } from 'lucide-react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './build-flow-stepper.module.scss';

export type { BuildStep };

const STEPS: ReadonlyArray<{ id: BuildStep; label: string }> = [
  { id: 'conversation', label: 'Conversation' },
  { id: 'day-picks', label: 'Day picks' },
  { id: 'send', label: 'Send' },
];

const styles = {
  root: pickCss(css, css.root),
  item: pickCss(css, css.item),
  step: pickCss(css, css.step),
  num: pickCss(css, css.num),
  label: pickCss(css, css.label),
  sep: pickCss(css, css.sep),
};

type BuildFlowStepperProps = {
  active: BuildStep;
  onStepClick?: (step: BuildStep) => void;
};

/**
 * BuildFlowStepper — pen frame `Xzqfh`. The 3-step Conversation › Day picks ›
 * Send header shown across every screen of the thread-driven build flow.
 * Steps before `active` render as completed (check), steps after as pending.
 */
export const BuildFlowStepper = ({
  active,
  onStepClick,
}: BuildFlowStepperProps) => {
  const activeIndex = STEPS.findIndex((s) => s.id === active);

  return (
    <nav aria-label="Build progress" className={styles.root()}>
      {STEPS.map((step, i) => {
        const state: 'done' | 'active' | 'pending' =
          i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'pending';
        return (
          <div className={styles.item()} key={step.id}>
            {i > 0 && (
              <span aria-hidden="true" className={styles.sep()}>
                ›
              </span>
            )}
            <button
              aria-current={state === 'active' ? 'step' : undefined}
              className={joinCss(styles.step({ state }))}
              disabled={!onStepClick || state === 'pending'}
              onClick={() => onStepClick?.(step.id)}
              type="button"
            >
              <span className={styles.num()}>
                {state === 'done' ? (
                  <Check aria-hidden="true" size={9} />
                ) : (
                  i + 1
                )}
              </span>
              <span className={styles.label()}>{step.label}</span>
            </button>
          </div>
        );
      })}
    </nav>
  );
};
